/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';

import { useQuery } from '@apollo/client';
import { compose } from 'recompose';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import Retracked from 'js/app/retracked';
import * as fullStory from 'js/lib/fullstory';
import { getCmsLocaleString } from 'js/lib/language';
import { useLocation, useParams } from 'js/lib/useRouter';
import { isSuperuser } from 'js/lib/user';
import type UserAgent from 'js/lib/useragent';

import { LoadingSection } from '@coursera/cds-core';

import { ProductVariantNames } from 'bundles/browse/constants';
import FullStoryExperiments from 'bundles/epic/clients/FullStory';
import GrowthPremiumExperiments from 'bundles/epic/clients/GrowthPremium';
import type {
  MastertrackPageContentQuery,
  MastertrackPageContentQueryVariables,
} from 'bundles/premium-certificate/__generated__/contentfulTypes';
import CertificateOverview from 'bundles/premium-certificate/components/CertificateOverview';
import CertificateRecommendations from 'bundles/premium-certificate/components/CertificateRecommendations';
import MtcUcert404 from 'bundles/premium-certificate/components/MtcUcert404';
import type { AtAGlanceDisplayListType } from 'bundles/premium-certificate/components/mastertrack/AtAGlanceSection';
import AtAGlanceSection from 'bundles/premium-certificate/components/mastertrack/AtAGlanceSection';
import CertificateBanner from 'bundles/premium-certificate/components/mastertrack/CertificateBanner';
import CertificateCourses from 'bundles/premium-certificate/components/mastertrack/CertificateCourses';
import CertificateDisclaimer from 'bundles/premium-certificate/components/mastertrack/CertificateDisclaimer';
import CertificateFAQs from 'bundles/premium-certificate/components/mastertrack/CertificateFAQs';
import CertificateInstructorsList from 'bundles/premium-certificate/components/mastertrack/CertificateInstructorsList';
import CertificateNavigationBar from 'bundles/premium-certificate/components/mastertrack/CertificateNavigationBar';
import EarnCreditSection from 'bundles/premium-certificate/components/mastertrack/EarnCreditSection';
import ProgramDescription from 'bundles/premium-certificate/components/mastertrack/ProgramDescription';
import type {
  GetMastertrackBySlugQuery,
  GetMastertrackBySlugQueryVariables,
} from 'bundles/premium-certificate/queries/__generated__/getMastertrackBySlug';
import { org_coursera_catalogp_degree_CatalogAvailabilityStatus as AVAILABILITY_STATUS } from 'bundles/premium-certificate/queries/__generated__/globalTypes';
import { getMastertrackPageContentBySlug } from 'bundles/premium-certificate/queries/contentfulQueries';
import getMastertrackBySlug from 'bundles/premium-certificate/queries/getMastertrackBySlug.graphql';
import { CourseraMetatags } from 'bundles/seo';
import { getSubdomainDetails } from 'bundles/seo/utils/domainAndMetadataUtils';

import _t from 'i18n!nls/premium-certificate';

const styles = {
  loadingContainer: () => css`
    height: 80vh;
  `,
};

type InputProps = {
  userAgent: UserAgent;
};

type PropsFromMTDP = {
  domainIds?: string[];
  productVariant?: string;
};

type Props = InputProps & PropsFromMTDP;

type NavItem = { id: string; label: string };

const isMastertrackAvailable = (availabilityStatus?: string): boolean => {
  return !!availabilityStatus && (availabilityStatus === AVAILABILITY_STATUS.LIVE || isSuperuser());
};

const MTDPPage = (props: Props): React.ReactElement => {
  const { domainIds, productVariant, userAgent } = props;
  const { slug } = useParams();
  const location = useLocation();
  const isPreview = Number(location.query.preview) === 1;

  // PremiumProductCollections data
  const { data, loading } = useQuery<GetMastertrackBySlugQuery, GetMastertrackBySlugQueryVariables>(
    getMastertrackBySlug,
    {
      context: {
        clientName: 'gatewayGql',
      },
      variables: {
        slug,
      },
      skip: !slug,
      errorPolicy: 'all',
    }
  );
  const availabilityStatus = data?.PremiumProductCollections?.getBySlug?.catalogAvailabilityStatus;
  const partnerCatalogData = data?.PremiumProductCollections?.getBySlug?.partners[0];
  const mastertrackId = data?.PremiumProductCollections?.getBySlug?.id.replace(/mastertrack~/g, 'credentialTrack~');

  // Contentful data
  const { data: pageContentData, loading: pageContentLoading } = useQuery<
    MastertrackPageContentQuery,
    MastertrackPageContentQueryVariables
  >(getMastertrackPageContentBySlug, {
    variables: {
      slug,
      locale: getCmsLocaleString(),
      preview: isPreview,
    },
    skip: !slug || !isMastertrackAvailable(availabilityStatus),
    // Only send a request through the Preview API if the user can preview
    context: { clientName: isPreview ? 'contentfulPreviewGql' : 'contentfulGql' },
    errorPolicy: 'all',
  });
  const pageContent = pageContentData?.premiumCertificatePageContentCollection?.items[0];

  const [isScrollPastBanner, setScrollPastBanner] = useState(false);
  const [isEmbeddedRegFormDisplayed, setEmbeddedRegFormDisplayed] = useState(false);

  // The Graduate Certificate programs (as of writing there are only 4) do not show up in the
  // PremiumProductCollections response. I'm blocking this experiment from running on those pages,
  // and only allowing this to be visible on MasterTracks and University Certificates for now.
  const allowedProductTypes: string[] = [ProductVariantNames.UniversityCertificate, ProductVariantNames.Mastertrack];
  const shouldDisplayCertRecommendations =
    productVariant &&
    allowedProductTypes.includes(productVariant) &&
    GrowthPremiumExperiments.get('showCertRecommendationsOnCertPages');

  useEffect(() => {
    if (FullStoryExperiments.get('enableFullstoryOnMtdp')) {
      fullStory.start();
    }
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const certificateBannerEl = document.getElementById('CertificateBanner');

      if (!certificateBannerEl) return;

      const bannerBottom = certificateBannerEl.getBoundingClientRect().bottom;
      if (bannerBottom < 0) {
        setScrollPastBanner(true);
      } else {
        setScrollPastBanner(false);
      }
    };

    window.addEventListener('scroll', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [slug]);

  const insertAdditionalNavigationElements = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (navItems: NavItem[], index: number, ...newNavItems: NavItem[]) => {
      if (!newNavItems.length) return navItems;
      return [...navItems.slice(0, index), ...newNavItems, ...navItems.slice(index)];
    },
    []
  );

  if (!pageContent) {
    if (pageContentLoading || loading) {
      return (
        <div css={styles.loadingContainer}>
          <LoadingSection />
        </div>
      );
    }
    return <MtcUcert404 productType="mastertrack" />;
  }

  if (!isMastertrackAvailable(availabilityStatus)) {
    return <MtcUcert404 productType="mastertrack" />;
  }

  const {
    certificateName,
    certificateType,
    partnerInformation,
    bannerDescription,
    expressionOfInterestContainer,
    enrollmentChecklistHeading,
    enrollmentChecklistItemsCollection,
    overviewTitle,
    overviewValuePropOne,
    overviewValuePropOneIcon,
    overviewValuePropTwo,
    overviewValuePropTwoIcon,
    overviewValuePropThree,
    overviewValuePropThreeIcon,
    overviewVisual,
    programDescriptionTitle,
    programDescriptionSubtitle,
    requiredBackground,
    skillsYouWillGain,
    overviewDescription,
    atAGlance,
    earnCreditTitle,
    earnCreditDescription,
    earnCreditTowardsText,
    earnCreditFinePrint,
    stackableDestinationName,
    stackableDestinationUrl,
    isStackableDestination100Online,
    faqsCollection,
    faqContactInfo,
    disclaimer,
    coursesTitle,
    coursesListCollection,
    instructorTitle,
    instructorsCollection,
    seoInformation,
    navigationElementsCollection,
  } = pageContent;

  const atAGlanceSectionDisplayList =
    atAGlance &&
    ([
      {
        title: atAGlance.programEnrollmentDate,
        subtitle: atAGlance.programStartDate,
        detailsTitle: _t('Future dates'),
        details: atAGlance.programFutureDates,
      },
      {
        title: atAGlance.timeFrame,
        subtitle: atAGlance.hoursPerWeek,
      },
      {
        title: atAGlance.tuition,
        subtitle: atAGlance.tuitionCaption,
        detailsTitle: _t('Payment details'),
        details: atAGlance.paymentDetails,
      },
      {
        title: atAGlance.location,
        subtitle: atAGlance.locationCaption,
      },
    ] as AtAGlanceDisplayListType);

  const showEarnCreditSection = !!(stackableDestinationName && stackableDestinationUrl);
  const showEarnCreditNavItem = showEarnCreditSection
    ? [
        {
          id: 'credit',
          label: _t('Earn Credit'),
        },
      ]
    : [];

  const showCertRecsNavItem = shouldDisplayCertRecommendations
    ? [
        {
          id: 'recommendations',
          label: _t('Recommended'),
        },
      ]
    : [];

  const defaultNavigationElements = [
    {
      id: 'overview',
      label: _t('Overview'),
    },
    {
      id: 'program',
      label: _t('Program'),
    },
    {
      id: 'courses',
      label: _t('Courses'),
    },
    {
      id: 'instructors',
      label: _t('Instructors'),
    },
    {
      id: 'faq',
      label: _t('FAQ'),
    },
  ];

  const navigationElements = navigationElementsCollection?.items.length
    ? (navigationElementsCollection.items as NavItem[])
    : defaultNavigationElements;

  const ADDITIONAL_NAV_INDEX = 3;
  const allNavigationElements = insertAdditionalNavigationElements(
    navigationElements,
    ADDITIONAL_NAV_INDEX,
    ...[...showEarnCreditNavItem, ...showCertRecsNavItem]
  );
  const { isInternationalSubdomain } = getSubdomainDetails(location);

  return (
    <main data-test="MTDPPage">
      <CertificateBanner
        slug={slug}
        mastertrackId={mastertrackId}
        certificateName={certificateName}
        certificateType={certificateType}
        bannerDescription={bannerDescription}
        partnerInformation={partnerInformation}
        expressionOfInterestContainer={expressionOfInterestContainer}
        enrollmentChecklistHeading={enrollmentChecklistHeading}
        enrollmentChecklistItemsCollection={enrollmentChecklistItemsCollection}
        isScrollPastBanner={isScrollPastBanner}
        setEmbeddedRegFormDisplayed={setEmbeddedRegFormDisplayed}
      />
      <CertificateNavigationBar
        pageSections={allNavigationElements}
        isScrollPastBanner={isScrollPastBanner}
        isEmbeddedRegFormDisplayed={isEmbeddedRegFormDisplayed}
        userAgent={userAgent}
      />
      <div id="overview">
        <AtAGlanceSection displayList={atAGlanceSectionDisplayList} />
        <CertificateOverview
          overviewTitle={overviewTitle}
          overviewValuePropOne={overviewValuePropOne}
          overviewValuePropOneIcon={overviewValuePropOneIcon}
          overviewValuePropTwo={overviewValuePropTwo}
          overviewValuePropTwoIcon={overviewValuePropTwoIcon}
          overviewValuePropThree={overviewValuePropThree}
          overviewValuePropThreeIcon={overviewValuePropThreeIcon}
          overviewVisual={overviewVisual}
        />
      </div>
      <ProgramDescription
        programDescriptionTitle={programDescriptionTitle}
        requiredBackground={requiredBackground}
        programDescriptionSubtitle={programDescriptionSubtitle}
        skillsYouWillGain={skillsYouWillGain}
        overviewDescription={overviewDescription}
      />
      <CertificateCourses coursesTitle={coursesTitle} coursesListCollection={coursesListCollection} />
      {shouldDisplayCertRecommendations && (
        <CertificateRecommendations
          domainIds={domainIds}
          productVariant={productVariant}
          slug={slug}
          title={_t('People interested in this #{programType} also viewed:', {
            programType:
              productVariant === ProductVariantNames.UniversityCertificate
                ? _t('University Certificate')
                : _t('MasterTrack® Certificate'),
          })}
        />
      )}
      <EarnCreditSection
        earnCreditTitle={earnCreditTitle}
        earnCreditDescription={earnCreditDescription}
        earnCreditTowardsText={earnCreditTowardsText}
        earnCreditFinePrint={earnCreditFinePrint}
        certificateName={certificateName}
        certificateType={certificateType}
        stackableDestinationName={stackableDestinationName}
        stackableDestinationUrl={stackableDestinationUrl}
        isStackableDestination100Online={isStackableDestination100Online}
        partnerInformation={partnerInformation}
        partnerCatalogData={partnerCatalogData}
        userAgent={userAgent}
      />
      <CertificateInstructorsList
        instructorTitle={instructorTitle}
        instructorsCollection={instructorsCollection}
        userAgent={userAgent}
      />
      <CertificateFAQs faqsCollection={faqsCollection} faqContactInfo={faqContactInfo} />
      <CertificateDisclaimer disclaimer={disclaimer} />
      <CourseraMetatags
        metaNameAndProperties={{
          title: seoInformation?.metaTitle ?? undefined,
          description: seoInformation?.metaDescription ?? undefined,
          enableViewportFitCover: true,
          disableCrawlerIndexing: isInternationalSubdomain,
        }}
        hrefLangOverrides={() => []}
      />
    </main>
  );
};

export default compose<Props, {}>(
  Retracked.createTrackedContainer(() => ({
    namespace: {
      app: 'premium_certificate',
      page: 'mastertrack',
    },
  })),
  connectToStores<{}, { userAgent: UserAgent }>(['ApplicationStore'], ({ ApplicationStore }) => ({
    userAgent: ApplicationStore.getUserAgent(),
  }))
)(MTDPPage);
